import React from 'react';
import { E, useDisplay } from '../../logic/imports';
import * as Fmt from '../../shared/formatters';

/*
    KeyValueDisplay
*/

interface KeyValueDisplayProps {
    name: string;
    value: string | number;
    keyStyleOverride?: React.CSSProperties;
    valueStyleOverride?: React.CSSProperties;
    /** How to display the value, if it is numeric */
    numericValueFormat?: 'dollars' | 'percentage';
    roundedValue?: boolean;
    forceCents?: boolean;
}

export const KeyValueDisplay: React.FC<KeyValueDisplayProps> = (props) => {
    // Subscribe to display preferences to trigger re-render when showCents changes
    const { prefs } = useDisplay();

    let formattedValue: string | number;

    // handle formatting numeric value
    if (props.numericValueFormat && typeof props.value === 'number') {
        // Use formatter which respects showCents user preference
        formattedValue = props.numericValueFormat === 'dollars' 
            ? Fmt.formatDollar(props.value, props.forceCents ?? prefs.showCents)
            : Fmt.formatPercent(props.value, props.forceCents ?? prefs.showCents);
    }
    // no formatting chosen, use value as-is
    else {
        // error - format was set with non-numeric value. warn in console, but use the value as-is.
        if (props.numericValueFormat && typeof props.value !== 'number') {
            console.warn(`KeyValueDisplay: numericValueFormat "${props.numericValueFormat}" `
                + `requires numeric value, got ${typeof props.value}:`, props.value);
        }
        formattedValue = props.value;
    }

    return (
        <div className="kvf-row">
            <div className="kvf-key" style={props.keyStyleOverride}>
                {props.name}
            </div>
            <div className="kvf-value" style={props.valueStyleOverride}>
                {formattedValue}
            </div>
        </div>
    );
}

/*
    KeyValueInputField
*/

interface KeyValueInputProps<T extends string | number> {
    name: string;
    value: T;
    placeholder?: string;
    onChange?: (value: T) => void;
    onBlur?: (value: T) => void;
    min?: number;
    max?: number;
    keyStyleOverride?: React.CSSProperties;
    inputStyleOverride?: React.CSSProperties;
    displayFormat?: 'dollars' | 'percentage';
}

export const KeyValueInput = <T extends string | number>(props: KeyValueInputProps<T>) => {
    const isNumber = props.min !== undefined;
    // const isNumber = typeof props.value === 'number';
    const [displayValue, setDisplayValue] = React.useState<string>(String(props.value));
    const [isFocused, setIsFocused] = React.useState<boolean>(false);
    const prevPropsValueRef = React.useRef<T>(props.value);
    
    // Sync displayValue with props.value when value changes from parent (not on blur)
    React.useEffect(() => {
        if (!isFocused && props.value !== prevPropsValueRef.current) {
            setDisplayValue(String(props.value));
            prevPropsValueRef.current = props.value;
        }
    }, [props.value, isFocused]);
    
    const getFormattedValue = (value: string | number, focused: boolean): string => {
        if (focused || !props.displayFormat) {
            return String(value);
        }

        const numValue = typeof value === 'number' ? value : parseFloat(value);
        if (isNaN(numValue)) return String(value);

        if (props.displayFormat === 'dollars') {
            return `$${numValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
        } else if (props.displayFormat === 'percentage') {
            return `${numValue}%`;
        }

        return String(value);
    };
    
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        
        if (isNumber) {
            // For numbers, validate format but don't enforce min/max yet
            if (inputValue === '' || /^-?\d*\.?\d*$/.test(inputValue)) {
                setDisplayValue(inputValue);
                props.onChange?.(parseFloat(inputValue) as T);
            }
        } else {
            // For strings, allow any input
            setDisplayValue(inputValue);
            props.onChange?.(inputValue as T);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isNumber) return; // String fields allow all keys

        const key = e.key;
        const currentValue = e.currentTarget.value;
        const selectionStart = e.currentTarget.selectionStart;
        const selectionEnd = e.currentTarget.selectionEnd;
        const hasSelection = selectionStart !== selectionEnd;

        // Allow control keys
        if (['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Enter', 'Escape'].includes(key)) {
            return;
        }

        // Allow minus only at the start
        if (key === '-') {
            if (currentValue.length === 0 || currentValue[0] === '-') {
                return; // Allow if empty or already has minus at start
            }
            e.preventDefault();
            return;
        }

        // Allow period (decimal) only once, unless text is selected (will be replaced)
        if (key === '.') {
            if (hasSelection || !currentValue.includes('.')) {
                return; // Allow if text selected or if first period
            }
            e.preventDefault();
            return;
        }

        // Allow digits
        if (/[0-9]/.test(key)) {
            return;
        }

        // Block everything else
        e.preventDefault();
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(false);

        if (isNumber) {
            const inputValue = displayValue;
            
            if (inputValue === '') {
                // Empty field: reset to original value
                setDisplayValue(String(props.value));
                props.onBlur?.(props.value);
                return;
            }

            let numValue = parseFloat(inputValue);

            // Apply min/max constraints
            if (props.min !== undefined && numValue < props.min) {
                numValue = props.min;
            }
            if (props.max !== undefined && numValue > props.max) {
                numValue = props.max;
            }

            setDisplayValue(String(numValue));
            props.onBlur?.(numValue as T);
        } else {
            // String field: just call onBlur with current value
            props.onBlur?.(displayValue as T);
        }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(true);
        e.target.select();
    };

    const displayedValue = isFocused 
        ? (displayValue === '' ? '' : displayValue)
        : getFormattedValue(displayValue === '' ? props.value : displayValue, false);

    return (
        <div className='kvf-row'>
            <div className='kvf-key' style={props.keyStyleOverride}>
                {props.name}
            </div>

            <input
                className="kvf-dropdown"
                style={props.inputStyleOverride}
                type="text"
                placeholder={props.placeholder}
                inputMode={isNumber ? 'numeric' : 'text'}
                value={displayedValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                onBlur={handleBlur}
            />
        </div>
    );
}

/*
    KeyValueCheckbox
*/

interface KeyValueCheckboxProps {
    name: string;
    value: boolean;
    onChange: (value: boolean) => void;
    keyStyleOverride?: React.CSSProperties;
    checkboxStyleOverride?: React.CSSProperties;
}

export const KeyValueCheckbox: React.FC<KeyValueCheckboxProps> = (props) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        props.onChange(e.target.checked);
    };

    return (
        <div className="kvf-row">
            <div className="kvf-key" style={props.keyStyleOverride}>
                {props.name}
            </div>
            <input
                type="checkbox"
                checked={props.value}
                onChange={handleChange}
                style={{
                    cursor: 'pointer',
                    width: '18px',
                    height: '18px',
                    ...props.checkboxStyleOverride
                }}
            />
        </div>
    );
}

/*
    KeyValueDropdown
*/

interface DropdownOption<T extends string | number> {
    label: string;
    value: T;
}

interface KeyValueDropdownProps<T extends string | number> {
    name: string;
    value: T;
    options: DropdownOption<T>[];
    onChange: (value: T) => void;
    keyStyleOverride?: React.CSSProperties;
    selectStyleOverride?: React.CSSProperties;
}

export const KeyValueDropdown = <T extends string | number>(props: KeyValueDropdownProps<T>) => {
    return (
        <div className="kvf-row">
            <div className="kvf-key" style={props.keyStyleOverride}>{props.name}</div>
            
            <select
                className="kvf-dropdown"
                value={props.value}
                onChange={e => props.onChange(e.target.value as T)}
                style={props.selectStyleOverride}
            >
                {props.options.map(option => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
        </div>
    );
}

/*
    FillBarDouble
*/

interface SegmentedBarProps {
    segments: {
        value: number;
        label: string;
        color: string;
    }[],
    maxWidth: number,
    showPercentages: boolean
}

export const SegmentedBar: React.FC<SegmentedBarProps> = (props) => {
    // Calculate total and percentages
    const total = props.segments.reduce((sum, segment) => sum + segment.value, 0);
    const count = props.segments.length;
    const segmentWidth = 100 / count;
    
    // Determine segment positions and styles in advance of rendering the
    // different components
    const segmentsWithMetadata = props.segments.map((segment, index) => {
        const isFirst = index === 0;
        const isLast = index === count - 1;
        let percentage = total > 0 ? Math.round((segment.value / total) * 100) : 0;

        // Calculate border radius based on position
        let borderRadius = '0';
        if (count === 1 || percentage === 100) {
            borderRadius = '3px';
        } else if (isFirst) {
            borderRadius = '3px 0 0 3px';
        } else if (isLast) {
            borderRadius = '0 3px 3px 0';
        }
        
        // Calculate text alignment
        const textAlign: React.CSSProperties['textAlign'] = isFirst ? 'left' : isLast ? 'right' : 'center';
        
        return {
            ...segment,
            percentage,
            isFirst,
            isLast,
            borderRadius,
            textAlign
        };
    });

    // Make sure percentages add up to 100
    let accumulatedPercentage = 0;
    segmentsWithMetadata.forEach((segment) => {
        if (segment.isLast) {
            segment.percentage = 100 - accumulatedPercentage;
        } else {
            accumulatedPercentage += segment.percentage;
        }
    });

    return (
        <div className="fill-bar_container" style={{ maxWidth: props.maxWidth }}>
            
            {/* Visual Bar Segments */}
            <div className='fill-bar_segments-container'>
                {segmentsWithMetadata.map((segment, index) => (
                    <div
                        key={index}
                        style={{
                            background: segment.color,
                            borderRight: !segment.isLast ? '1px solid black' : 'none',
                            borderRadius: segment.borderRadius,
                            width: `${segment.percentage}%`,
                        }}
                    />
                ))}
            </div>

            {/* Segment Labels */}
            <div className='fill-bar_label'>
                {segmentsWithMetadata.map((segment, index) => (
                    <span
                        key={index}
                        style={{
                            color: segment.color,
                            minWidth: `${segmentWidth}%`,
                            textAlign: segment.textAlign
                        }}
                    >
                        {segment.label}
                        
                    </span>
                ))}
            </div>

            {/* Segment Values */}
            <div className="fill-bar_value">
                {segmentsWithMetadata.map((segment, index) => (
                    <span
                        key={index} 
                        style={{ 
                            color: segment.color,
                            minWidth: `${segmentWidth}%`,
                            textAlign: segment.textAlign
                        }}
                    >
                        ${segment.value.toLocaleString(undefined,
                            { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        &nbsp;
                        {props.showPercentages && 
                            `(${segment.percentage}%)`
                        }
                    </span>
                ))}
            </div>
                
            
        </div>
    );
}

/*
    NumericInputModal
*/

export interface NumericModalProps {
    header: string;
    body: string;
    minValue?: number;
    maxValue?: number;
    minMaxMessage?: string;
    onSubmit: (value: number) => void;
    onCancel?: () => void;
    isOpen: boolean;
}

export const NumericInputModal: React.FC<NumericModalProps> = (props) => {
    const [inputValue, setInputValue] = React.useState<string>('');

    const isValidValue = (): boolean => {
        if (inputValue === '') return false;
        const numValue = parseInt(inputValue, 10);
        const min = props.minValue ?? 0;
        const max = props.maxValue;

        if (isNaN(numValue) || numValue < min) return false;
        if (max !== undefined && numValue > max) return false;
        return true;
    };

    const handleSubmit = () => {
        if (!isValidValue()) return;

        const numValue = parseInt(inputValue, 10);
        props.onSubmit(numValue);
        setInputValue('');
    };

    const handleCancel = () => {
        props.onCancel?.();
        setInputValue('');
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && isValidValue()) {
            handleSubmit();
        } else if (e.key === 'Escape') {
            handleCancel();
        }
    };

    if (!props.isOpen) return null;

    return (
        <div className="modal-overlay" onClick={handleCancel}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">{props.header}</div>
                <div className="modal-body" style={{ whiteSpace: 'pre-wrap' }}>{props.body}</div>

                <div style={{ color: 'orange'}}>{props.minMaxMessage}</div>

                <input
                    type="text"
                    inputMode="numeric"
                    value={inputValue}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || /^\d+$/.test(val)) {
                            setInputValue(val);
                        }
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter value"
                    className="modal-input"
                    autoFocus
                />
                
                <div className="modal-footer">
                    <button className="button button--secondary" onClick={handleCancel}>
                        Cancel
                    </button>
                    <button 
                        className="button button--primary" 
                        onClick={handleSubmit}
                        disabled={!isValidValue()}
                    >
                        Submit
                    </button>
                </div>
            </div>
        </div>
    );
}

/*
    OrderRowDimmer - Dims and disables a row while waiting for async operations
*/

interface OrderRowDimmerProps {
    isActive: boolean;
}

export const OrderRowDimmer: React.FC<OrderRowDimmerProps> = (props) => {
    if (!props.isActive) return null;
    return <div className="orderRowDimmer" />;
};

/*
    PriceBar - Visual representation of price range with current price marker
    Shows a bar with two price targets (e.g., STOP and LIMIT for bracket orders)
    and a marker indicating where the current price sits within that range.
    Grays out the unreachable price if the order is filled.
*/

interface PriceBarProps {
    lowerPrice: number;     // Lower bound (STOP price)
    upperPrice: number;     // Upper bound (LIMIT price or TRAIL price)
    lowerLabel: 'STOP';
    upperLabel: 'LIMIT' | 'TRAIL';
    currentPrice: number;
    reference: E.PriceReference;
    status: E.OrderStatus;
}

export const PriceBar: React.FC<PriceBarProps> = (props) => {
    const { lowerPrice, upperPrice, lowerLabel, upperLabel, currentPrice, reference, status } = props;
    const isTrailing = upperLabel === 'TRAIL';

    // Check if order status is terminal
    const isTerminal = [
        E.OrderStatus.CANCELLED,
        E.OrderStatus.FILLED,
        E.OrderStatus.EXPIRED,
        E.OrderStatus.REJECTED
    ].includes(status);

    // Determine the currency symbol based on reference type
    const currencySymbol = reference === E.PriceReference.PERCENT ? '%' : '$';

    // Format price for display
    const formatPrice = (price: number): string => {
        const formatted = currencySymbol === '%' ? price.toFixed(0) : price.toFixed(2);
        return currencySymbol === '%' ? `${formatted}%` : `$${formatted}`;
    };

    // For trailing orders, upperPrice is the trailDistance, so display price is stop + distance
    const displayUpperPrice = isTrailing ? lowerPrice + upperPrice : upperPrice;

    // Normalize min/max prices (for puts, upperPrice might be less than lowerPrice)
    const minPrice = Math.min(lowerPrice, displayUpperPrice);
    const maxPrice = Math.max(lowerPrice, displayUpperPrice);
    const isInverted = lowerPrice > displayUpperPrice;  // True when prices are backwards (e.g., PUT with STOCK ref)

    // Determine if order is filled (never for trailing orders, which are always active)
    const isFilled = !isTrailing && (currentPrice > maxPrice || currentPrice < minPrice);
    const hitUpper = !isTrailing && currentPrice > maxPrice;   // Upper bound was hit
    const hitLower = !isTrailing && currentPrice < minPrice;   // Lower bound was hit

    // Calculate marker position as a percentage (0-100)
    // For inverted prices, calculate backwards so marker position matches visual position
    let markerPosition = 50;  // default middle
    const range = maxPrice - minPrice;
    if (range > 0) {
        if (isInverted) {
            // For inverted: marker position based on distance from lowerPrice (which is visually on the left)
            markerPosition = ((lowerPrice - currentPrice) / range) * 100;
        } else {
            // For normal: marker position based on distance from minPrice
            markerPosition = ((currentPrice - minPrice) / range) * 100;
        }
        markerPosition = Math.max(0, Math.min(100, markerPosition));  // Clamp to 0-100
    }

    // Color scheme: gray out the unreachable side when filled
    // For inverted prices (puts), still color by original lower/upper labels, not min/max
    let lowerBoxColor = '#d60000ff';  // default red for STOP
    let upperBoxColor = '#459221ff';  // default green for LIMIT
    
    // If terminal status, set both to gray
    if (isTerminal) {
        lowerBoxColor = '#666';
        upperBoxColor = '#666';
    } else {
        if (isTrailing) {
            upperBoxColor = '#2e73dbff';  // blue for TRAIL
        }
        
        // If filled, gray out the unreachable label (trailing orders never fill)
        if (isFilled) {
            if (hitUpper) {
                // Price went above max, so the lower label is unreachable
                lowerBoxColor = '#666';
            } else if (hitLower) {
                // Price went below min, so the upper label is unreachable
                upperBoxColor = '#666';
            }
        }
    }

    const labelStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4px 8px',
        borderRadius: '5px',
        minWidth: '60px',
        whiteSpace: 'nowrap',
    }

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
        }}>
            {/* Lower price box (STOP) */}
            <div style={{...labelStyle, backgroundColor: lowerBoxColor}}>
                {formatPrice(lowerPrice)}
            </div>

            {/* Price bar with marker */}
            <div style={{
                position: 'relative',
                flex: 1,
                height: '24px',
                backgroundColor: '#333',
                borderRadius: '3px',
                border: '1px solid #555',
                display: 'flex',
                alignItems: 'center',
            }}>
                {/* Marker line (hide if terminal status) */}
                {!isTerminal && (
                    <div style={{
                        position: 'absolute',
                        left: `${markerPosition}%`,
                        top: 0,
                        bottom: 0,
                        width: '3px',
                        backgroundColor: '#ffa600ff',
                        transform: 'translateX(-50%)'
                    }} />
                )}
            </div>

            {/* Upper price box (LIMIT or TRAIL) */}
            <div style={{...labelStyle, backgroundColor: upperBoxColor}}>
                {isTrailing ? '+ ' : ''}
                {formatPrice(isTrailing ? upperPrice : displayUpperPrice)}
            </div>
        </div>
    );
};